import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Save, Check } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function SystemEmailConfig() {
  const [config, setConfig] = useState({ resend_key: "", sender_email: "", reply_to: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { authAxios } = useAuth();

  useEffect(() => {
    authAxios.get("/platform/system-email-config")
      .then(r => setConfig(r.data))
      .catch(err => console.error("Request failed:", err?.message || err))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put("/platform/system-email-config", config);
      toast.success("System email configuration saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  return (
    <div data-testid="system-email-config">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="w-5 h-5 text-[#1e3a5f]" />
        <h2 className="text-lg font-bold text-[#1e3a5f]">System Email Configuration</h2>
      </div>

      <p className="text-slate-500 text-sm mb-4">
        Configure the email service used for system-level notifications (e.g., FA account creation, password resets from Sys Admin).
        Each tenant has its own email config managed under the Tenants tab.
      </p>

      <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Resend API Key</label>
          <input
            value={config.resend_key || ""}
            onChange={e => setConfig({...config, resend_key: e.target.value})}
            type="password"
            placeholder="re_..."
            className="w-full px-4 py-3 rounded-xl border text-sm font-mono"
          />
          <p className="text-[10px] text-slate-400 mt-1">Get your key from <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline">resend.com</a></p>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Sender Email</label>
          <input
            value={config.sender_email || ""}
            onChange={e => setConfig({...config, sender_email: e.target.value})}
            type="email"
            placeholder="notifications@your-domain.org"
            className="w-full px-4 py-3 rounded-xl border text-sm"
          />
          <p className="text-[10px] text-slate-400 mt-1">Must be verified in your Resend account. Emails are sent "from" this address.</p>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-600 mb-1 block">Reply-To Email</label>
          <input
            value={config.reply_to || ""}
            onChange={e => setConfig({...config, reply_to: e.target.value})}
            type="email"
            placeholder="admin@your-domain.org"
            className="w-full px-4 py-3 rounded-xl border text-sm"
          />
          <p className="text-[10px] text-slate-400 mt-1">When recipients reply to system emails, replies go to this address.</p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 rounded-xl bg-[#1e3a5f] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="save-email-config"
        >
          {saving ? <Save className="w-4 h-4 animate-pulse" /> : <Check className="w-4 h-4" />}
          {saving ? "Saving..." : "Save Configuration"}
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mt-4">
        <p className="text-xs text-blue-700 font-medium mb-1">How email routing works:</p>
        <ul className="text-xs text-blue-600 space-y-1 list-disc pl-4">
          <li><strong>System emails</strong> (FA creation, sys admin notifications): Uses this configuration</li>
          <li><strong>Tenant emails</strong> (member onboarding, verification codes, expense notifications): Uses the tenant's own email config (Tenants tab → Edit → Email fields)</li>
          <li>If a tenant has no email config, it falls back to this system config</li>
        </ul>
      </div>
    </div>
  );
}
